//
// Copyright © 2022 Hardcore Engineering Inc.
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
//

import { ActivityMessage, Reaction } from '@hcengineering/activity'
import {
  PersonId,
  Class,
  Doc,
  DocumentQuery,
  type Domain,
  IdMap,
  Markup,
  Mixin,
  Ref,
  Space,
  Timestamp,
  TxCUD,
  TxOperations,
  AccountUuid,
  Tx,
  Type,
  AnyAttribute,
  AttachedDoc
} from '@hcengineering/core'
import type { Asset, IntlString, Metadata, Plugin, Resource } from '@hcengineering/platform'
import { plugin } from '@hcengineering/platform'
import { Preference } from '@hcengineering/preference'
import { IntegrationType } from '@hcengineering/setting'
import { AnyComponent, Location, ResolvedLocation } from '@hcengineering/ui'
import { Action } from '@hcengineering/view'
import { PersonSpace } from '@hcengineering/contact'

import { Readable, Writable } from './types'

export type * from './types'
export * from './utils'

export const DOMAIN_NOTIFICATION = 'notification' as Domain
export const DOMAIN_DOC_NOTIFY = 'notification-dnc' as Domain
export const DOMAIN_USER_NOTIFY = 'notification-user' as Domain
export const DOMAIN_READ_STATE = 'notification-read-state' as Domain

/**
 * @public
 */
export interface BrowserNotification extends Doc {
  user: AccountUuid
  title: IntlString
  body: IntlString
  intlParams: Record<string, any>
  intlParamsNotLocalized?: Record<string, IntlString>
  onClickLocation?: Location
  sender: PersonId
  tag: Ref<Doc>
  messageId?: Ref<ActivityMessage>
  messageClass?: Ref<Class<ActivityMessage>>
  objectId: Ref<Doc>
  objectClass: Ref<Class<Doc>>
  soundAlert: boolean
}

export interface PushData {
  tag?: string
  title: string
  body: string
  icon?: string
  domain?: string
  url?: string
}

export interface PushSubscriptionKeys {
  p256dh: string
  auth: string
}

export interface PushSubscription extends Doc {
  user: AccountUuid
  endpoint: string
  keys: PushSubscriptionKeys
  name?: string
}

export interface PushSubscriptionSetting extends Preference {
  attachedTo: Ref<PushSubscription>
  enabled: boolean
}

/**
 * @public
 */
export interface NotificationGroup extends Doc {
  label: IntlString
  icon: Asset
  parent?: Ref<NotificationGroup>
  // using for autogenerated settings
  objectClass?: Ref<Class<Doc>>
  order?: number
}

/**
 * @public
 */
export interface NotificationPreferencesGroup extends Doc {
  label: IntlString
  icon: Asset
  presenter: AnyComponent
}

/**
 * @public
 */
export interface NotificationTemplate {
  text: IntlString
  html: IntlString
  subject: IntlString
}

/**
 * @public
 */
export interface NotificationContent {
  title: IntlString
  body: IntlString
  intlParams: Record<string, string | number>
  intlParamsNotLocalized?: Record<string, IntlString>
}

/**
 * @public
 */
export interface NotificationType extends Doc {
  group: Ref<NotificationGroup>
  subGroup?: Ref<NotificationGroup>

  generated: boolean
  hidden: boolean

  label: IntlString
  templates?: NotificationTemplate

  defaultEnabled: boolean

  objectClass: Ref<Class<Doc>>
  attachedToClass?: Ref<Class<Doc>>

  notifyAuthor?: boolean

  priority?: number

  notificationMessage?: IntlString
  isMention?: boolean
}

export interface MessageNotificationType<T extends ActivityMessage = ActivityMessage> extends NotificationType {
  messageClass: Ref<Class<T>>
  attachedToClass: Ref<Class<Doc>>
  match?: DocumentQuery<T>

  // Use for DocUpdateMessage
  field?: string
  attribute?: Ref<AnyAttribute>
}

export interface TxNotificationType extends NotificationType {
  txClasses: Ref<Class<Tx>>[]
  field?: string
  attrTypes?: Ref<Class<Type<Doc>>>[]
  match?: DocumentQuery<Tx>
  attachToParent?: boolean
}

export interface NotificationProvider extends Doc {
  label: IntlString
  description: IntlString
  icon: Asset
  defaultEnabled: boolean
  depends?: Ref<NotificationProvider>
  canDisable: boolean
  ignoreAll?: boolean
  order: number
  presenter?: AnyComponent
  isAvailableFn?: Resource<() => boolean>
}

export interface NotificationProviderDefaults extends Doc {
  provider: Ref<NotificationProvider>
  excludeIgnore?: Ref<NotificationType>[]
  ignoredTypes: Ref<NotificationType>[]
  enabledTypes: Ref<NotificationType>[]
}

export interface NotificationProviderSetting extends Preference {
  attachedTo: Ref<NotificationProvider>
  enabled: boolean
}

export interface NotificationTypeSetting extends Preference {
  attachedTo: Ref<NotificationProvider>
  type: Ref<NotificationType>
  enabled: boolean
}

/**
 * @public
 */
export interface NotificationObjectPresenter extends Class<Doc> {
  presenter: AnyComponent
}

/**
 * @public
 */
export const notificationId = 'notification' as Plugin

/**
 * @public
 */
export interface NotificationPreview extends Class<Doc> {
  presenter: AnyComponent
}

/**
 * @public
 */
export interface NotificationContextPresenter extends Class<Doc> {
  labelPresenter?: AnyComponent
}

/**
 * @public
 */
export interface InboxNotification extends Doc<PersonSpace> {
  user: AccountUuid
  isViewed: boolean

  docNotifyContext: Ref<DocNotifyContext>
  objectId: Ref<Doc>
  objectClass: Ref<Class<Doc>>

  // For browser notifications
  title?: IntlString
  body?: IntlString
  data?: Markup
  intlParams?: Record<string, string | number>
  intlParamsNotLocalized?: Record<string, IntlString>
  archived: boolean

  allowedProviders: Record<Ref<NotificationProvider>, Ref<NotificationType>[]>
}

export interface ActivityInboxNotification extends InboxNotification {
  attachedTo: Ref<ActivityMessage>
  attachedToClass: Ref<Class<ActivityMessage>>
}

export interface CommonInboxNotification extends InboxNotification {
  header?: IntlString
  headerIcon?: Asset
  headerObjectId?: Ref<Doc>
  headerObjectClass?: Ref<Class<Doc>>
  message?: IntlString
  markup?: Markup
  icon?: Asset
  iconProps?: Record<string, any>
}

export interface ReactionInboxNotification extends CommonInboxNotification {
  emoji: string
  ref: Ref<Reaction>
  attachedTo: Ref<ActivityMessage>
  attachedToClass: Ref<Class<ActivityMessage>>
}

export interface MentionInboxNotification extends CommonInboxNotification {
  mentionedIn: Ref<Doc>
  mentionedInClass: Ref<Class<Doc>>
}

export interface DisplayActivityInboxNotification extends ActivityInboxNotification {
  combinedIds: Ref<ActivityInboxNotification>[]
  combinedMessages: ActivityMessage[]
}

export type DisplayInboxNotification = DisplayActivityInboxNotification | InboxNotification

/**
 * @public
 */

export type DocNotificationMode = 'all' | 'mentions' | 'mute'

export interface DocNotifyContext extends Doc<PersonSpace> {
  user: AccountUuid
  // Context
  objectId: Ref<Doc>
  objectClass: Ref<Class<Doc>>
  objectSpace: Ref<Space>

  lastView?: Timestamp
  lastUpdate?: Timestamp
  lastNotify?: Timestamp
  lastNotifiedMessage?: Timestamp

  // Only for debug
  tx?: Ref<TxCUD<Doc>>

  settings?: {
    mode?: DocNotificationMode
  }
}

// eslint-disable-next-line @typescript-eslint/consistent-indexed-object-style
export interface ReadState extends AttachedDoc {
  [key: AccountUuid]: ReadPosition
}

export interface ReadPosition {
  messageId: Ref<ActivityMessage>
  timestamp: Timestamp
}

/**
 * @public
 */
export interface InboxNotificationsClient {
  contextByDoc: Writable<Map<Ref<Doc>, DocNotifyContext>>
  contexts: Writable<DocNotifyContext[]>
  contextById: Readable<IdMap<DocNotifyContext>>

  readStateByDoc: Readable<Map<Ref<Doc>, ReadState | null>>

  inboxNotifications: Readable<InboxNotification[]>
  activityInboxNotifications: Writable<ActivityInboxNotification[]>
  inboxNotificationsByContext: Readable<Map<Ref<DocNotifyContext>, InboxNotification[]>>

  readDoc: (_id: Ref<Doc>) => Promise<void>
  forceReadDoc: (doc: Doc) => Promise<void>
  readNotifications: (client: TxOperations, ids: Array<Ref<InboxNotification>>) => Promise<void>
  removeAllNotifications: () => Promise<void>
  readAllNotifications: () => Promise<void>

  getReadState: (attachedTo: Ref<Doc>) => Promise<ReadState | undefined>
  loadReadState: (attachedTo: Ref<Doc>) => Promise<void>
}

/**
 * @public
 */
export type InboxNotificationsClientFactory = () => InboxNotificationsClient

/**
 * @public
 */
export interface ActivityNotificationViewlet extends Doc {
  messageMatch: DocumentQuery<Doc>
  presenter: AnyComponent
}

/**
 * @public
 */
export type NotifyFunc = (title: string, body: string, _id?: string, onClick?: () => void) => void

export interface NotificationAppearancePreference extends Preference {
  showChatBadge: boolean
}

/**
 * @public
 */
const notification = plugin(notificationId, {
  mixin: {
    NotificationObjectPresenter: '' as Ref<Mixin<NotificationObjectPresenter>>,
    NotificationPreview: '' as Ref<Mixin<NotificationPreview>>,
    NotificationContextPresenter: '' as Ref<Mixin<NotificationContextPresenter>>
  },
  class: {
    NotificationType: '' as Ref<Class<NotificationType>>,
    MessageNotificationType: '' as Ref<Class<MessageNotificationType>>,
    TxNotificationType: '' as Ref<Class<TxNotificationType>>,

    InboxNotification: '' as Ref<Class<InboxNotification>>,
    ActivityInboxNotification: '' as Ref<Class<ActivityInboxNotification>>,
    CommonInboxNotification: '' as Ref<Class<CommonInboxNotification>>,
    MentionInboxNotification: '' as Ref<Class<MentionInboxNotification>>,
    ReactionInboxNotification: '' as Ref<Class<ReactionInboxNotification>>,

    BrowserNotification: '' as Ref<Class<BrowserNotification>>,
    PushSubscription: '' as Ref<Class<PushSubscription>>,
    PushSubscriptionSetting: '' as Ref<Class<PushSubscriptionSetting>>,
    NotificationGroup: '' as Ref<Class<NotificationGroup>>,
    NotificationPreferencesGroup: '' as Ref<Class<NotificationPreferencesGroup>>,
    DocNotifyContext: '' as Ref<Class<DocNotifyContext>>,

    ActivityNotificationViewlet: '' as Ref<Class<ActivityNotificationViewlet>>,

    NotificationProvider: '' as Ref<Class<NotificationProvider>>,
    NotificationTypeSetting: '' as Ref<Class<NotificationTypeSetting>>,
    NotificationProviderSetting: '' as Ref<Class<NotificationProviderSetting>>,
    NotificationProviderDefaults: '' as Ref<Mixin<NotificationProviderDefaults>>,

    ReadState: '' as Ref<Class<ReadState>>,
    NotificationAppearancePreference: '' as Ref<Class<NotificationAppearancePreference>>
  },
  ids: {
    NotificationSettings: '' as Ref<Doc>,
    NotificationGroup: '' as Ref<NotificationGroup>,
    NotificationAppearancePreferencesGroup: '' as Ref<NotificationPreferencesGroup>,
    MeAddedInCollaboratorsNotification: '' as Ref<MessageNotificationType>,
    MeRemovedFromCollaboratorsNotification: '' as Ref<MessageNotificationType>,
    MentionNotificationType: '' as Ref<TxNotificationType>
  },
  metadata: {
    PushPublicKey: '' as Metadata<string>
  },
  providers: {
    InboxNotificationProvider: '' as Ref<NotificationProvider>,
    PushNotificationProvider: '' as Ref<NotificationProvider>,
    SoundNotificationProvider: '' as Ref<NotificationProvider>
  },
  integrationType: {
    MobileApp: '' as Ref<IntegrationType>
  },
  component: {
    Inbox: '' as AnyComponent,
    NotificationPresenter: '' as AnyComponent,
    CollaboratorsChanged: '' as AnyComponent,
    DocNotifyContextPresenter: '' as AnyComponent,
    NotificationCollaboratorsChanged: '' as AnyComponent,
    GeneralPreferencesGroup: '' as AnyComponent,
    WebpushesPreferencesPresenter: '' as AnyComponent,
    MutePopup: '' as AnyComponent,
    NotificationAppearancePreferencesPresenter: '' as AnyComponent
  },
  action: {
    ReadNotifyContext: '' as Ref<Action>,
    RemoveContextNotifications: '' as Ref<Action>
  },
  icon: {
    Notifications: '' as Asset,
    Inbox: '' as Asset,
    BellCrossed: '' as Asset,
    Appearance: '' as Asset
  },
  sound: {
    InboxNotification: '' as Asset
  },
  string: {
    Appearance: '' as IntlString,
    Notification: '' as IntlString,
    Notifications: '' as IntlString,
    DontTrack: '' as IntlString,
    Inbox: '' as IntlString,
    CommonNotificationTitle: '' as IntlString,
    CommonNotificationTitleWithIdentifier: '' as IntlString,
    MessageNotificationBody: '' as IntlString,
    UpdateNotificationBody: '' as IntlString,
    ChangedCollaborators: '' as IntlString,
    NewCollaborators: '' as IntlString,
    RemovedCollaborators: '' as IntlString,
    Edited: '' as IntlString,
    Pinned: '' as IntlString,
    All: '' as IntlString,
    ClearAll: '' as IntlString,
    MarkReadAll: '' as IntlString,
    RemoveAllConfirmationTitle: '' as IntlString,
    RemoveAllConfirmationMessage: '' as IntlString,
    YouAddedCollaborators: '' as IntlString,
    YouRemovedCollaborators: '' as IntlString,
    Push: '' as IntlString,
    HasNewNotifications: '' as IntlString,
    UnreadNotificationsCount: '' as IntlString,
    General: '' as IntlString,
    InboxNotificationsDescription: '' as IntlString,
    PushNotificationsDescription: '' as IntlString,
    CommonNotificationCollectionAdded: '' as IntlString,
    CommonNotificationCollectionRemoved: '' as IntlString,
    SoundNotificationsDescription: '' as IntlString,
    Sound: '' as IntlString,
    NoAccessToObject: '' as IntlString,
    ViewIn: '' as IntlString,
    Clear: '' as IntlString,
    YouAddedAsCollaborator: '' as IntlString,
    YouRemovedFromCollaborators: '' as IntlString,
    Webpushes: '' as IntlString,
    UnknownDevice: '' as IntlString,
    RemoveWebpush: '' as IntlString,
    WebpushRemoveConfirm: '' as IntlString,
    Value: '' as IntlString,
    Current: '' as IntlString,
    PushOnDesktop: '' as IntlString,
    AlreadySubscribed: '' as IntlString,
    PushNotConfigured: '' as IntlString,
    PushNotSupported: '' as IntlString,
    PushDenied: '' as IntlString,
    PushSubscribeError: '' as IntlString,
    PushSubscribeErrorPermissionDenied: '' as IntlString,
    PushSubscribeErrorNetwork: '' as IntlString,
    PushSubscribeErrorNotSupported: '' as IntlString,
    PushSubscribeErrorDefault: '' as IntlString,
    Subscribe: '' as IntlString,
    AllNotifications: '' as IntlString,
    JustMentions: '' as IntlString,
    Mute: '' as IntlString,
    EditNotifications: '' as IntlString,
    AddMeInCollaborators: '' as IntlString,
    RemoveMeFromCollaborators: '' as IntlString,
    ShowChatBadge: '' as IntlString
  },
  emailTemplate: {
    MeAddedInCollaboratorsNotificationText: '' as IntlString,
    MeAddedInCollaboratorsNotificationHtml: '' as IntlString,
    MeAddedInCollaboratorsNotificationSubject: '' as IntlString,
    MeRemovedFromCollaboratorsNotificationText: '' as IntlString,
    MeRemovedFromCollaboratorsNotificationHtml: '' as IntlString,
    MeRemovedFromCollaboratorsNotificationSubject: '' as IntlString,
    MentionNotificationText: '' as IntlString,
    MentionNotificationHtml: '' as IntlString,
    MentionNotificationSubject: '' as IntlString,
    GeneratedNotificationText: '' as IntlString,
    GeneratedNotificationHtml: '' as IntlString,
    GeneratedNotificationSubject: '' as IntlString
  },
  function: {
    Notify: '' as Resource<NotifyFunc>,
    CheckPushPermission: '' as Resource<(value: boolean) => Promise<boolean>>,
    GetInboxNotificationsClient: '' as Resource<InboxNotificationsClientFactory>,
    HasInboxNotifications: '' as Resource<
      (notificationsByContext: Map<Ref<DocNotifyContext>, InboxNotification[]>) => Promise<boolean>
    >,
    IsNotificationAllowed: '' as Resource<(type: NotificationType, providerId: Ref<NotificationProvider>) => boolean>,
    EditDocNotificationsVisibilityTester: '' as Resource<(doc: Doc | Doc[] | undefined) => Promise<boolean>>
  },
  resolver: {
    Location: '' as Resource<(loc: Location) => Promise<ResolvedLocation | undefined>>
  }
})

export default notification
